// src/screens/admin/orders/AdminOrdersManagement.jsx
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import {
  FiSearch,
  FiX,
  FiStar,
  FiClipboard,
  FiCheckCircle,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import AdminMasterLayout from "../../../layouts/admin/AdminMasterLayout";
import AppContext from "../../../contexts/AppContext";
import "../../../assets/css/admin/screens/ordersManagement/AdminOrdersManagement.css";

const StarRating = ({ rating, size = 12 }) => (
  <div className="aom-review-stars">
    {[1, 2, 3, 4, 5].map((star) => (
      <FiStar
        key={star}
        size={size}
        className={star <= rating ? "aom-star-filled" : "aom-star-empty"}
      />
    ))}
  </div>
);

const ReviewModal = ({ review, customerName, orderId, onClose, baseUrlForFiles }) => {
  if (!review) return null;
  return (
    <>
      <div className="aom-review-modal-backdrop" onClick={onClose} />
      <div className="aom-review-modal">
        <div className="aom-review-modal-header">
          <div>
            <h3 className="aom-review-modal-title">Customer Review</h3>
            <span className="aom-review-modal-order">
              {orderId} — {customerName}
            </span>
          </div>
          <button className="aom-review-modal-close" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>
        <div className="aom-review-modal-meta">
          <StarRating rating={review.rating} size={16} />
          <span className="aom-review-modal-date">
            {review.created_at?.slice(0, 10)}
          </span>
        </div>

        {review.images?.length > 0 && (
          <div className="aom-review-modal-gallery">
            {review.images.map((img) => (
              <img
                key={img.id}
                src={`${baseUrlForFiles}/${img.image_path}`}
                alt="review attachment"
                className="aom-review-modal-image"
              />
            ))}
          </div>
        )}

        <p className="aom-review-modal-comment">"{review.comment}"</p>
      </div>
    </>
  );
};

const ReviewBadge = ({ review, onClick, baseUrlForFiles }) => {
  if (!review) return <span className="aom-no-review-badge">No Review</span>;
  const firstImage = review.images?.[0]?.image_path;
  const extraCount = (review.images?.length || 0) - 1;

  return (
    <button className="aom-review-badge-btn" onClick={onClick}>
      <div className="aom-review-badge-top">
        {firstImage && (
          <div className="aom-review-thumb-wrap">
            <img
              src={`${baseUrlForFiles}/${firstImage}`}
              alt="review"
              className="aom-review-thumb"
            />
            {extraCount > 0 && (
              <span className="aom-review-thumb-more">+{extraCount}</span>
            )}
          </div>
        )}
        <StarRating rating={review.rating} />
      </div>
      <span className="aom-review-excerpt">
        "{review.comment?.slice(0, 28)}
        {review.comment?.length > 28 ? "…" : ""}"
      </span>
    </button>
  );
};

const STATUS_OPTIONS = [
  "All Status",
  "Processing",
  "Shipped",
  "completed",
  "Cancelled",
];

const AdminOrdersManagement = () => {
  const [isDark, setIsDark] = useState(false);
  const navigate = useNavigate();
  const { baseUrl, baseUrlForFiles, setAdminSelectedOrder, adminAuthToken } =
    useContext(AppContext);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [statsData, setStatsData] = useState(null);
  const [reviewModal, setReviewModal] = useState(null);

  // Pagination (backend se aata hai)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const viewAllOrders = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/admin/order/viewAllOrders`, {
        params: { page: currentPage },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => {
        setOrders(response.data.orders ?? []);
        setTotalPages(response.data.last_page ?? 1);
        setTotalItems(response.data.total ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getStats = () => {
    axios
      .get(`${baseUrl}/admin/order/stats`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminAuthToken}`,
        },
      })
      .then((response) => setStatsData(response.data))
      .catch(() => {});
  };

  // currentPage jab bhi change ho, backend se woh page mangwao
  useEffect(() => {
    viewAllOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    getStats();
  }, []);

  const handleViewOrder = (order) => {
    setAdminSelectedOrder(order);
    navigate("/admin/orders/order-detail");
  };

  // Search aur status filter sirf isi page pe load hue orders mein hoti hai (frontend filter)
  const getFilteredOrders = () => {
    let filtered = orders;
    if (statusFilter !== "All Status") {
      filtered = filtered.filter((o) => o.status === statusFilter);
    }
    if (search.trim()) {
      filtered = filtered.filter(
        (o) =>
          o.id?.toString().includes(search) ||
          o.full_name?.toLowerCase().includes(search.toLowerCase()) ||
          o.seller?.store_name?.toLowerCase().includes(search.toLowerCase())
      );
    }
    return filtered;
  };

  const filteredOrders = getFilteredOrders();
  const hasCompletedOrders = filteredOrders.some(
    (o) => o.status === "completed"
  );

  // Max 5 page-number buttons visible
  const getVisiblePageNumbers = () => {
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    const pages = [];
    for (let page = startPage; page <= endPage; page++) pages.push(page);
    return pages;
  };

  return (
    <AdminMasterLayout>
      <div className={`aom-card-shell ${isDark ? "dark-mode" : ""}`}>

        {/* Stat Cards */}
        <div className="aom-overview">
          <div className="aom-overview-card">
            <div>
              <span className="aom-overview-label">Total Orders</span>
              <h3 className="aom-overview-value">
                {statsData?.total_orders ?? "0"}
              </h3>
              <p className="aom-overview-sub">All orders on platform</p>
            </div>
            <div className="aom-overview-icon">
              <FiClipboard />
            </div>
          </div>
          <div className="aom-overview-card">
            <div>
              <span className="aom-overview-label">Completed</span>
              <h3 className="aom-overview-value">
                {statsData?.completed_orders ?? "0"}
              </h3>
              <p className="aom-overview-sub">Successfully delivered</p>
            </div>
            <div className="aom-overview-icon">
              <FiCheckCircle />
            </div>
          </div>
          <div className="aom-overview-card">
            <div>
              <span className="aom-overview-label">Cancelled</span>
              <h3 className="aom-overview-value">
                {statsData?.cancelled_orders ?? "0"}
              </h3>
              <p className="aom-overview-sub">Needs attention</p>
            </div>
            <div className="aom-overview-icon aom-overview-icon-alert">
              <FiAlertCircle />
            </div>
          </div>
        </div>

        {/* Header / Filters */}
        <div className="aom-header">
          <div className="aom-header-left" />
          <div className="aom-header-filters">
            <div className="aom-search">
              <FiSearch size={15} />
              <input
                placeholder="Search by order, customer or shop"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  className="aom-clear-input-btn"
                  onClick={() => setSearch("")}
                >
                  <FiX size={12} />
                </button>
              )}
            </div>
            <select
              className="aom-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="aom-table-wrapper">
          <table className="aom-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Shop</th>
                <th>Date</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                {hasCompletedOrders && <th>Review</th>}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={hasCompletedOrders ? 9 : 8}
                    className="text-center py-4"
                  >
                    <Spinner
                      animation="border"
                      size="sm"
                      className="aom-spinner"
                      style={{ color: "#ff8559" }}
                    />
                    <span className="ms-2 aom-muted aom-loading-text">
                      Loading orders...
                    </span>
                  </td>
                </tr>
              )}

              {!loading && filteredOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={hasCompletedOrders ? 9 : 8}
                    className="aom-no-results-row"
                  >
                    No orders found.
                  </td>
                </tr>
              )}

              {!loading &&
                filteredOrders.length > 0 &&
                filteredOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="aom-order-id-cell">#{order.id}</td>
                    <td>
                      <div className="aom-cell-main">
                        <div className="aom-thumb aom-thumb-orange">
                          {order.full_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="aom-cell-title">{order.full_name}</p>
                          <span className="aom-cell-sub">{order.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td>{order.seller?.store_name || "—"}</td>
                    <td>
                      {new Date(order.created_at).toLocaleDateString("en-PK", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td>
                      {(order.cart || []).reduce(
                        (sum, i) => sum + i.quantity,
                        0
                      )}
                    </td>
                    <td className="aom-total-cell">
                      Rs {Number(order.total_amount).toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={`aom-status ${
                          order.status === "Processing"
                            ? "aom-status-processing"
                            : order.status === "Shipped"
                            ? "aom-status-shipped"
                            : order.status === "completed"
                            ? "aom-status-delivered"
                            : order.status === "Cancelled"
                            ? "aom-status-cancel"
                            : ""
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    {hasCompletedOrders && (
                      <td>
                        {order.status === "completed" ? (
                          <ReviewBadge
                            review={order.review || null}
                            baseUrlForFiles={baseUrlForFiles}
                            onClick={() =>
                              order.review &&
                              setReviewModal({
                                review: order.review,
                                customerName: order.full_name,
                                orderId: `#${order.id}`,
                              })
                            }
                          />
                        ) : (
                          <span className="aom-review-na-badge">—</span>
                        )}
                      </td>
                    )}
                    <td>
                      <button
                        className="aom-link"
                        onClick={() => handleViewOrder(order)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          {/* Pagination controls */}
          {!loading && filteredOrders.length > 0 && totalPages > 1 && (
            <div className="aom-pagination-bar">
              <span className="aom-pagination-info">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
              </span>
              <div className="aom-pagination-controls">
                <button
                  className="aom-page-btn aom-page-btn--nav"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <FiChevronLeft size={14} />
                </button>
                {getVisiblePageNumbers().map((page) => (
                  <button
                    key={page}
                    className={`aom-page-btn ${page === currentPage ? "aom-page-btn--active" : ""}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="aom-page-btn aom-page-btn--nav"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  <FiChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {reviewModal && (
        <ReviewModal
          review={reviewModal.review}
          customerName={reviewModal.customerName}
          orderId={reviewModal.orderId}
          baseUrlForFiles={baseUrlForFiles}
          onClose={() => setReviewModal(null)}
        />
      )}
    </AdminMasterLayout>
  );
};

export default AdminOrdersManagement;